import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Grid from '@mui/material/Unstable_Grid2';
import Button from '@mui/material/Button';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { useAuth } from 'react-oidc-context';
import { useSnackbar } from 'notistack';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import cloudApi from '../../app/api/cloudApi';
import profileApi from '../../app/api/profileApi';
import AccessRuleBuilder from './skillko-settings/components/AccessRuleBuilder';
import AccessRuleCard from './skillko-settings/components/AccessRuleCard';
import ExpiryRuleBuilder from './skillko-settings/components/ExpiryRuleBuilder';
import ConfirmDialog from './skillko-settings/components/ConfirmDialog';
import { parseRuleForCertIds, ruleHasExpiryCheck, ruleHasIsActiveCheck } from './skillko-settings/components/AccessRuleBuilder';
import { useTranslations } from '../../hooks/use-translations';

// ---------------------------------------------------------------------------
// Tab panel wrapper
// ---------------------------------------------------------------------------
function TabPanel({ children, value, index }) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: 3 }}>
      {value === index && children}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// User identity helper
// ---------------------------------------------------------------------------
function getUserIdentifier(user) {
  if (!user?.profile) return 'Unknown User';
  return (
    user.profile.preferred_username ||
    user.profile.email ||
    user.profile.name ||
    user.profile.sub ||
    'Unknown User'
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------
export default function SkillkoSettingsPage() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslations();
  const builderRef = useRef(null);

  // Page-level state
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState(null);
  const [site, setSite] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  // Team state
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('');

  // Certificates (shared between tabs)
  const [certificates, setCertificates] = useState([]);
  const [loadingCertificates, setLoadingCertificates] = useState(false);

  // Access rules
  const [existingRules, setExistingRules] = useState([]);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [editingRule, setEditingRule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);

  // Expiry rules (one row per team in DB; UI treats as one site rule + team checkboxes)
  const [expiryRulesForSite, setExpiryRulesForSite] = useState([]);
  const [loadingExpiryRule, setLoadingExpiryRule] = useState(false);
  const [expiryVerifying, setExpiryVerifying] = useState(false);

  // Auto expiry bulk action
  const [enablingAutoExpiry, setEnablingAutoExpiry] = useState(false);

  // Auto-induction settings (per site)
  const [autoInductionSettings, setAutoInductionSettings] = useState({
    isEnabled: false,
    defaultAccessLevelId: '',
    defaultAntiPassback: true,
  });
  const [savingAutoInduction, setSavingAutoInduction] = useState(false);
  const [accessLevelsForSite, setAccessLevelsForSite] = useState([]);

  // Queue full induction readiness recalculation for the site (worker)
  const [recalculatingInduction, setRecalculatingInduction] = useState(false);

  // Confirmation dialog
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

  // -----------------------------------------------------------------------
  // Certificates: full account catalogue (union across Skillko teams)
  // -----------------------------------------------------------------------
  const loadAllCertificates = useCallback(async (siteData) => {
    if (!siteData || !auth.user) return;
    setLoadingCertificates(true);
    try {
      const resp = await cloudApi.getSkillkoCertificatesForAccount(auth.user.access_token, {
        siteId: siteData.id,
        accountId: siteData.accountId,
      });
      setCertificates(resp?.data || []);
    } catch {
      enqueueSnackbar(t('sites.skillko.failedLoadCertificates'), { variant: 'error' });
      setCertificates([]);
    } finally {
      setLoadingCertificates(false);
    }
  }, [auth.user?.access_token, enqueueSnackbar, t]);

  // -----------------------------------------------------------------------
  // Initialization (once per site + access token; avoid unstable deps like `t`)
  // -----------------------------------------------------------------------
  useEffect(() => {
    const accessToken = auth.user?.access_token;

    if (!auth.isAuthenticated || !accessToken || !siteId) {
      setError(!siteId ? t('sites.skillko.siteIdRequired') : t('sites.skillko.loginRequired'));
      setPageLoading(false);
      return;
    }

    let cancelled = false;

    const init = async () => {
      setPageLoading(true);
      setError(null);

      try {
        const siteResp = await cloudApi.getSite(accessToken, siteId);
        if (cancelled) return;
        if (!siteResp.data) throw new Error(t('sites.skillko.siteDataNotFound'));
        setSite(siteResp.data);

        const teamsResp = await cloudApi.getSkillkoTeams(accessToken, {
          siteId: siteResp.data.id,
          accountId: siteResp.data.accountId,
        });
        if (cancelled) return;
        const teamsList =
          teamsResp?.data?.map((team) => ({ id: team.id, name: team.name })) || [];
        setTeams(teamsList);

        await loadAllCertificates(siteResp.data);
        if (cancelled) return;

        try {
          const expResp = await cloudApi.getExpiryRulesForSite(accessToken, {
            siteId: siteResp.data.id,
            accountId: siteResp.data.accountId,
          });
          if (!cancelled) {
            setExpiryRulesForSite(Array.isArray(expResp.data) ? expResp.data : []);
          }
        } catch {
          if (!cancelled) setExpiryRulesForSite([]);
        }

        const rulesResp = await cloudApi.getRulesForSite(accessToken, siteId);
        if (!cancelled) setExistingRules(rulesResp?.data || []);

        try {
          const metadataResp = await profileApi.getCreateProfileMetadata(
            accessToken,
            siteResp.data.id,
            siteResp.data.accountId
          );
          if (!cancelled) {
            setAccessLevelsForSite(metadataResp?.data?.accessLevels || []);
          }
        } catch {
          if (!cancelled) setAccessLevelsForSite([]);
        }

        try {
          const autoInductionResp = await cloudApi.getAutoInductionSettingsForSite(accessToken, {
            siteId: siteResp.data.id,
            accountId: siteResp.data.accountId,
          });
          if (!cancelled && autoInductionResp?.data) {
            setAutoInductionSettings({
              isEnabled: !!autoInductionResp.data.isEnabled,
              defaultAccessLevelId: autoInductionResp.data.defaultAccessLevelId || '',
              defaultAntiPassback: autoInductionResp.data.defaultAntiPassback ?? true,
            });
          }
        } catch {
          // Keep defaults if this fails; the page itself should not fail to load.
        }
      } catch (err) {
        if (!cancelled) {
          setError(`${t('sites.skillko.failedInitializePage')}: ${err.message || t('sites.skillko.unknownError')}`);
          enqueueSnackbar(t('sites.skillko.errorLoadingPageData'), { variant: 'error' });
        }
      } finally {
        if (!cancelled) setPageLoading(false);
      }
    };

    init();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init only when site or token changes
  }, [auth.isAuthenticated, auth.user?.access_token, siteId]);


  const loadExpiryRulesForSite = useCallback(async () => {
    if (!site || !auth.user) return;
    setLoadingExpiryRule(true);
    try {
      const resp = await cloudApi.getExpiryRulesForSite(auth.user.access_token, {
        siteId,
        accountId: site.accountId,
      });
      setExpiryRulesForSite(Array.isArray(resp.data) ? resp.data : []);
    } catch (err) {
      if (err.response?.status !== 404) {
        enqueueSnackbar('Failed to load expiry rules', { variant: 'error' });
      }
      setExpiryRulesForSite([]);
    } finally {
      setLoadingExpiryRule(false);
    }
  }, [site, auth.user?.access_token, siteId, enqueueSnackbar]);

  // -----------------------------------------------------------------------
  // Data loaders
  // -----------------------------------------------------------------------
  const reloadRules = async () => {
    try {
      const resp = await cloudApi.getRulesForSite(auth.user.access_token, siteId);
      setExistingRules(resp?.data || []);
    } catch {
      enqueueSnackbar('Failed to reload rules', { variant: 'error' });
    }
  };

  // -----------------------------------------------------------------------
  // Team change
  // -----------------------------------------------------------------------
  const handleTeamChange = (teamId) => {
    setSelectedTeam(teamId);
    setEditingRuleId(null);
    setEditingRule(null);
  };

  // -----------------------------------------------------------------------
  // Certificate name resolver
  // -----------------------------------------------------------------------
  const getCertNameById = useCallback(
    (id) => {
      const cert = certificates.find((c) => c.productCatalogueId === id);
      return cert ? cert.name : `Unknown (${id})`;
    },
    [certificates]
  );

  const getTeamLabel = useCallback(
    (teamId) => {
      if (teamId == null) return null;
      const t = teams.find((x) => String(x.id) === String(teamId));
      return t?.name ?? `Team ${teamId}`;
    },
    [teams]
  );

  // -----------------------------------------------------------------------
  // Access rule handlers
  // -----------------------------------------------------------------------
  const handleVerifyAccessRule = async (ruleCode) => {
    setVerifying(true);
    try {
      const resp = await cloudApi.validateRule(auth.user.access_token, { ruleCode });
      if (resp.data?.isValid) {
        enqueueSnackbar('Rule code is valid!', { variant: 'success' });
        return true;
      }
      enqueueSnackbar(resp.data?.errorMessage || 'Rule validation failed', { variant: 'error' });
      return false;
    } catch (err) {
      enqueueSnackbar(err.response?.data?.errorMessage || 'Error verifying rule', { variant: 'error' });
      return false;
    } finally {
      setVerifying(false);
    }
  };

  const handleSaveAccessRule = async ({ ruleName, ruleCode, editingRuleId: ruleId, targetTeamIds }) => {
    setLoading(true);
    const userIdentifier = getUserIdentifier(auth.user);

    try {
      if (ruleId) {
        const originalTeamId = editingRule?.teamId;
        const targets = targetTeamIds?.length ? targetTeamIds : [];
        if (targets.length === 0) {
          enqueueSnackbar('Select teams to apply this rule to', { variant: 'warning' });
          return;
        }
        if (originalTeamId == null) {
          enqueueSnackbar('Cannot save: missing team on edited rule', { variant: 'error' });
          return;
        }

        const stillHasOriginal = targets.some((t) => String(t) === String(originalTeamId));

        if (stillHasOriginal) {
          const teamObj = teams.find((t) => String(t.id) === String(originalTeamId));
          const teamName = teamObj?.name || originalTeamId;
          await cloudApi.updateRule(auth.user.access_token, {
            id: ruleId,
            accountId: site.accountId,
            name: ruleName,
            description: `Skillko team rule for ${teamName}`,
            ruleCode,
            isEnabled: true,
            order: 0,
            updatedBy: userIdentifier,
          });
        } else {
          await cloudApi.deleteRule(auth.user.access_token, {
            ruleId,
            accountId: site.accountId,
          });
        }

        let created = 0;
        let failed = 0;
        for (const tid of targets) {
          if (String(tid) === String(originalTeamId) && stillHasOriginal) {
            continue;
          }
          const teamObj = teams.find((t) => String(t.id) === String(tid));
          const teamName = teamObj?.name || tid;
          try {
            await cloudApi.createRule(auth.user.access_token, {
              siteId,
              name: ruleName,
              description: `Skillko team rule for ${teamName}`,
              ruleCode,
              isEnabled: true,
              order: 0,
              createdBy: userIdentifier,
              updatedBy: userIdentifier,
              teamId: tid,
            });
            created += 1;
          } catch {
            failed += 1;
          }
        }

        const parts = [];
        if (stillHasOriginal) parts.push('Rule updated');
        else parts.push('Rule replaced for selected teams');
        if (created > 0) parts.push(`+${created} team rule(s) added`);
        if (failed > 0) parts.push(`${failed} failed`);
        parts.push('Profile readiness may take a moment to update.');
        enqueueSnackbar(parts.join(' · '), failed > 0 ? { variant: 'warning' } : { variant: 'success' });
      } else {
        const teamIds = targetTeamIds?.length ? targetTeamIds : [];
        if (teamIds.length === 0) {
          enqueueSnackbar('Select teams to apply this rule to', { variant: 'warning' });
          return;
        }
        let created = 0;
        let failed = 0;
        // Sequential awaits — avoid bursting the API; refresh runs in the background per save.
        for (const tid of teamIds) {
          const teamObj = teams.find((t) => String(t.id) === String(tid));
          const teamName = teamObj?.name || tid;
          try {
            await cloudApi.createRule(auth.user.access_token, {
              siteId,
              name: ruleName,
              description: `Skillko team rule for ${teamName}`,
              ruleCode,
              isEnabled: true,
              order: 0,
              createdBy: userIdentifier,
              updatedBy: userIdentifier,
              teamId: tid,
            });
            created += 1;
          } catch {
            failed += 1;
          }
        }
        if (created > 0) {
          enqueueSnackbar(
            failed
              ? `Rule created for ${created} team(s); ${failed} failed · Profile readiness may take a moment to update.`
              : `Rule created for ${created} team(s) · Profile readiness may take a moment to update.`,
            failed ? { variant: 'warning' } : { variant: 'success' }
          );
        } else {
          enqueueSnackbar('Failed to save rule for any team', { variant: 'error' });
        }
      }
      setEditingRuleId(null);
      setEditingRule(null);
      await reloadRules();
    } catch {
      enqueueSnackbar('Failed to save rule', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditRule = (rule) => {
    setEditingRuleId(rule.id);
    setEditingRule(rule);
    if (rule.teamId) setSelectedTeam(rule.teamId);
    setActiveTab(0);
    setTimeout(() => builderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleDeleteAccessRule = (rule) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Access Rule',
      message: `Are you sure you want to delete "${rule.name}"? This action cannot be undone.`,
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        setLoading(true);
        try {
          await cloudApi.deleteRule(auth.user.access_token, {
            ruleId: rule.id,
            accountId: site.accountId,
          });
          enqueueSnackbar('Rule deleted successfully', { variant: 'success' });
          await reloadRules();
        } catch {
          enqueueSnackbar('Failed to delete rule', { variant: 'error' });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // -----------------------------------------------------------------------
  // Expiry rule handlers
  // -----------------------------------------------------------------------
  const handleValidateExpiryRule = async (ruleCode) => {
    setExpiryVerifying(true);
    try {
      const resp = await cloudApi.validateExpiryRule(auth.user.access_token, { ruleCode });
      if (resp.data.success) {
        enqueueSnackbar('Expiry rule validated successfully!', { variant: 'success' });
        return true;
      }
      enqueueSnackbar(`Validation error: ${resp.data.error}`, { variant: 'error' });
      return false;
    } catch {
      enqueueSnackbar('Failed to validate expiry rule', { variant: 'error' });
      return false;
    } finally {
      setExpiryVerifying(false);
    }
  };

  const handleSaveExpiryRule = async ({ name, description, ruleCode, isEnabled, targetTeamIds }) => {
    if (!site) {
      enqueueSnackbar('Site data is missing', { variant: 'warning' });
      return;
    }
    const teamIds = targetTeamIds?.length ? targetTeamIds : [];
    if (teamIds.length === 0) {
      enqueueSnackbar('Select at least one team for this expiry rule', { variant: 'warning' });
      return;
    }
    const initialWithRule = new Set(
      (expiryRulesForSite || []).map((r) => String(r.teamId ?? r.teamID))
    );
    const targetSet = new Set(teamIds.map((t) => String(t)));
    setLoadingExpiryRule(true);
    const userName = auth.user.profile?.name || 'Unknown';
    let saved = 0;
    let deleted = 0;
    let failed = 0;
    try {
      // Sequential awaits — do not parallelize with Promise.all.
      for (const tid of initialWithRule) {
        if (!targetSet.has(tid)) {
          try {
            const delResp = await cloudApi.deleteExpiryRule(auth.user.access_token, {
              teamId: parseInt(tid, 10),
              siteId,
              accountId: site.accountId,
            });
            if (delResp.data?.success) deleted += 1;
            else failed += 1;
          } catch {
            failed += 1;
          }
        }
      }
      for (const tid of teamIds) {
        try {
          const resp = await cloudApi.createOrUpdateExpiryRule(auth.user.access_token, {
            teamId: parseInt(tid, 10),
            siteId,
            accountId: site.accountId,
            name,
            description,
            ruleCode,
            isEnabled,
            createdBy: userName,
            updatedBy: userName,
          });
          if (resp.data.success) saved += 1;
          else failed += 1;
        } catch {
          failed += 1;
        }
      }
      if (saved > 0 || deleted > 0) {
        enqueueSnackbar(
          failed
            ? `Saved for ${saved} team(s), removed from ${deleted}; ${failed} operation(s) failed`
            : `Expiry rule updated (${saved} team(s) saved${deleted ? `, ${deleted} removed` : ''})`,
          failed ? { variant: 'warning' } : { variant: 'success' }
        );
        await loadExpiryRulesForSite();
      } else {
        enqueueSnackbar('Failed to save expiry rule for any team', { variant: 'error' });
      }
    } catch {
      enqueueSnackbar('Failed to save expiry rule', { variant: 'error' });
    } finally {
      setLoadingExpiryRule(false);
    }
  };

  const handleDeleteExpiryRule = () => {
    if (!expiryRulesForSite?.length) return;
    setConfirmDialog({
      open: true,
      title: 'Delete expiry rules for this site',
      message:
        'This removes the default expiry rule for every Skillko team on this site that currently has one. Continue?',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        setLoadingExpiryRule(true);
        try {
          const resp = await cloudApi.deleteExpiryRulesForSite(auth.user.access_token, {
            siteId,
            accountId: site.accountId,
          });
          if (resp.data?.success) {
            setExpiryRulesForSite([]);
            enqueueSnackbar(
              `Removed expiry rules for this site (${resp.data.deleted ?? 0} team row(s)).`,
              { variant: 'success' }
            );
          } else {
            enqueueSnackbar('Failed to delete expiry rules', { variant: 'error' });
          }
        } catch {
          enqueueSnackbar('Failed to delete expiry rules', { variant: 'error' });
        } finally {
          setLoadingExpiryRule(false);
        }
      },
    });
  };

  // -----------------------------------------------------------------------
  // Auto expiry bulk action
  // -----------------------------------------------------------------------
  const handleEnableAutoExpiry = () => {
    setConfirmDialog({
      open: true,
      title: t('sites.skillko.enableAutoExpiryTitle'),
      message:
        t('sites.skillko.enableAutoExpiryMessage'),
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        setEnablingAutoExpiry(true);
        try {
          await cloudApi.enableAutoExpiryForSite(auth.user.access_token, {
            siteId,
            accountId: site.accountId,
          });
          enqueueSnackbar(
            t('sites.skillko.autoExpiryQueued'),
            { variant: 'success' }
          );
        } catch {
          enqueueSnackbar(t('sites.skillko.failedEnableAutoExpiry'), { variant: 'error' });
        } finally {
          setEnablingAutoExpiry(false);
        }
      },
    });
  };

  const handleRecalculateInductionReadiness = () => {
    setConfirmDialog({
      open: true,
      title: t('sites.skillko.recalculateInductionTitle'),
      message:
        t('sites.skillko.recalculateInductionMessage'),
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        setRecalculatingInduction(true);
        try {
          await cloudApi.refreshRulesForSite(auth.user.access_token, siteId);
          enqueueSnackbar(
            t('sites.skillko.inductionRecalculationQueued'),
            { variant: 'success' }
          );
        } catch {
          enqueueSnackbar(t('sites.skillko.failedQueueRecalculation'), { variant: 'error' });
        } finally {
          setRecalculatingInduction(false);
        }
      },
    });
  };

  // -----------------------------------------------------------------------
  // Auto-induction settings handlers
  // -----------------------------------------------------------------------
  const handleAutoInductionFieldChange = (field, value) => {
    setAutoInductionSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveAutoInductionSettings = async () => {
    if (!site) return;

    if (autoInductionSettings.isEnabled && !autoInductionSettings.defaultAccessLevelId) {
      enqueueSnackbar('Select a default access level before enabling automatic induction', { variant: 'warning' });
      return;
    }

    setSavingAutoInduction(true);
    try {
      const resp = await cloudApi.updateAutoInductionSettingsForSite(auth.user.access_token, {
        siteId,
        accountId: site.accountId,
        isEnabled: autoInductionSettings.isEnabled,
        defaultAccessLevelId: autoInductionSettings.defaultAccessLevelId || null,
        defaultAntiPassback: autoInductionSettings.defaultAntiPassback,
      });

      if (resp.data?.success) {
        enqueueSnackbar('Auto-induction settings saved', { variant: 'success' });
      } else {
        enqueueSnackbar(resp.data?.error || 'Failed to save auto-induction settings', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar(
        err.response?.data || 'Failed to save auto-induction settings',
        { variant: 'error' }
      );
    } finally {
      setSavingAutoInduction(false);
    }
  };

  // -----------------------------------------------------------------------
  // Error state
  // -----------------------------------------------------------------------
  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/sites')}>
          Return to Sites
        </Button>
      </Container>
    );
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <>
      <Helmet>
        <title>
          {site ? `${site.name} - ${t('sites.skillko.title')} | CloudLoq` : `${t('sites.skillko.title')} | CloudLoq`}
        </title>
      </Helmet>

      <Container maxWidth="xl">
        <Box sx={{ mb: 3 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
            <Link component={RouterLink} to="/sites" underline="hover" color="inherit">
              {t('navigation.sites')}
            </Link>
            {site && (
              <Typography color="text.primary">{site.name}</Typography>
            )}
            <Typography color="text.primary">{t('sites.skillko.title')}</Typography>
          </Breadcrumbs>
          <Typography variant="h4">{t('sites.skillko.title')}</Typography>
        </Box>

        {pageLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
                <Tab label={`${t('sites.skillko.accessRules')} (${existingRules.length})`} />
                <Tab label={t('sites.skillko.expiryRule')} />
                <Tab label={t('navigation.accountSettings')} />
                <Tab label="Auto-Induction" />
              </Tabs>
            </Box>

            {/* Tab 1: Access Rules */}
            <TabPanel value={activeTab} index={0}>
              <Grid container spacing={3}>
                <Grid xs={12} md={6}>
                  <Box ref={builderRef}>
                    <AccessRuleBuilder
                      certificates={certificates}
                      loadingCertificates={loadingCertificates}
                      teams={teams}
                      editingRuleId={editingRuleId}
                      editingRuleTeamId={editingRule?.teamId}
                      onCancelEdit={() => {
                        setEditingRuleId(null);
                        setEditingRule(null);
                      }}
                      onSave={handleSaveAccessRule}
                      onVerify={handleVerifyAccessRule}
                      onCancel={() => navigate('/sites')}
                      loading={loading}
                      verifying={verifying}
                      getCertNameById={getCertNameById}
                      initialRuleName={editingRule?.name || ''}
                      initialRuleCode={editingRule?.ruleCode || ''}
                      initialCertIds={
                        editingRule ? parseRuleForCertIds(editingRule.ruleCode) : []
                      }
                      initialIncludeExpiry={
                        editingRule ? ruleHasExpiryCheck(editingRule.ruleCode) : true
                      }
                      initialIncludeIsActive={
                        editingRule ? ruleHasIsActiveCheck(editingRule.ruleCode) : true
                      }
                    />
                  </Box>
                </Grid>

                <Grid xs={12} md={6}>
                  <Card>
                    <CardHeader title="Existing Rules" />
                    <Box sx={{ p: 3 }}>
                      {existingRules.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <Typography color="text.secondary" sx={{ mb: 1 }}>
                            No access rules have been created yet.
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Use the form on the left to create your first rule.
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {existingRules.map((rule) => (
                            <AccessRuleCard
                              key={rule.id}
                              rule={rule}
                              teamLabel={getTeamLabel(rule.teamId)}
                              editingRuleId={editingRuleId}
                              getCertNameById={getCertNameById}
                              onEdit={handleEditRule}
                              onDelete={handleDeleteAccessRule}
                              loading={loading}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>

            {/* Tab 2: Expiry Rule */}
            <TabPanel value={activeTab} index={1}>
              <Grid container spacing={3}>
                <Grid xs={12} md={8}>
                  <ExpiryRuleBuilder
                    teams={teams}
                    certificates={certificates}
                    getCertNameById={getCertNameById}
                    expiryRulesForSite={expiryRulesForSite}
                    onValidate={handleValidateExpiryRule}
                    onSave={handleSaveExpiryRule}
                    onDelete={handleDeleteExpiryRule}
                    verifying={expiryVerifying}
                    loadingExpiryRule={loadingExpiryRule}
                  />
                </Grid>
                <Grid xs={12} md={4}>
                  <Card sx={{ mt: 3 }}>
                    <CardHeader title="About Expiry Rules" />
                    <Box sx={{ p: 3 }}>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        The expiry rule determines when a worker's site access expires based on
                        their certificate expiry dates.
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        <strong>Groups:</strong> Each group selects the latest (max) expiry date
                        from its matching certificates. The final expiry is the earliest (min)
                        across all groups.
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        <strong>Fallback:</strong> When enabled, workers without matching
                        certificates receive a temporary expiry date (default 90 days from today).
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Filters:</strong> Each group can optionally require certificates to
                        be active, not expired, and/or approved.
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>

            {/* Tab 3: Account Settings */}
            <TabPanel value={activeTab} index={2}>
              <Grid container spacing={3}>
                <Grid xs={12} md={8}>
                  <Card>
                    <CardHeader title="Enable Auto Expiry for All Skillko Profiles" />
                    <Box sx={{ p: 3 }}>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        This is a <strong>one-time action</strong> that enables automatic expiry date
                        calculation for every non-deleted profile on this site that was created from
                        Skillko.
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Once enabled, each profile's expiry date will be recalculated using the
                        configured expiry rules and Skillko certificates. Future Skillko syncs will
                        automatically keep the expiry dates up to date.
                      </Typography>
                      <Alert severity="warning" sx={{ mb: 3 }}>
                        This operation will update all matching profiles in the background. Make sure
                        your expiry rules are configured correctly before proceeding.
                      </Alert>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleEnableAutoExpiry}
                        disabled={enablingAutoExpiry || !site}
                        startIcon={enablingAutoExpiry ? <CircularProgress size={18} /> : null}
                      >
                        {enablingAutoExpiry ? 'Processing...' : 'Enable Auto Expiry & Recalculate'}
                      </Button>
                    </Box>
                  </Card>

                  <Card sx={{ mt: 3 }}>
                    <CardHeader title="Recalculate access induction readiness" />
                    <Box sx={{ p: 3 }}>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        After you change <strong>access rules</strong> on the first tab, run this to update
                        induction readiness for all Skillko profiles on this site. Rule changes are saved to the
                        database immediately; this step applies them to workers in the background.
                      </Typography>
                      <Alert severity="info" sx={{ mb: 3 }}>
                        Use this when you are finished editing rules and want everyone&apos;s readiness flags
                        refreshed. Large sites may take several minutes.
                      </Alert>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleRecalculateInductionReadiness}
                        disabled={recalculatingInduction || !site}
                        startIcon={recalculatingInduction ? <CircularProgress size={18} /> : null}
                      >
                        {recalculatingInduction ? 'Queueing...' : 'Recalculate induction for this site'}
                      </Button>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>

            {/* Tab 4: Auto-Induction */}
            <TabPanel value={activeTab} index={3}>
              <Grid container spacing={3}>
                <Grid xs={12} md={8}>
                  <Card>
                    <CardHeader title="Automatic Induction" />
                    <Box sx={{ p: 3 }}>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        When enabled, Skillko profiles that become ready for induction on this site are
                        automatically registered - no one needs to click Register. Profiles that fail
                        validation (e.g. missing photo or email) are left as-is for manual registration
                        and retried automatically on the next Skillko sync.
                      </Typography>
                      <Alert severity="info" sx={{ mb: 3 }}>
                        This setting only applies to <strong>this site</strong>. Other sites are
                        unaffected.
                      </Alert>

                      <Stack spacing={3}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={autoInductionSettings.isEnabled}
                              onChange={(e) => handleAutoInductionFieldChange('isEnabled', e.target.checked)}
                            />
                          }
                          label="Enable automatic induction for this site"
                        />

                        <FormControl fullWidth disabled={accessLevelsForSite.length === 0}>
                          <InputLabel id="auto-induction-access-level-label">
                            Default access level
                          </InputLabel>
                          <Select
                            labelId="auto-induction-access-level-label"
                            label="Default access level"
                            value={autoInductionSettings.defaultAccessLevelId || ''}
                            onChange={(e) =>
                              handleAutoInductionFieldChange('defaultAccessLevelId', e.target.value)
                            }
                          >
                            <MenuItem value="">
                              <em>None selected</em>
                            </MenuItem>
                            {accessLevelsForSite.map((level) => (
                              <MenuItem key={level.id} value={level.id}>
                                {level.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <FormControlLabel
                          control={
                            <Switch
                              checked={autoInductionSettings.defaultAntiPassback}
                              onChange={(e) =>
                                handleAutoInductionFieldChange('defaultAntiPassback', e.target.checked)
                              }
                            />
                          }
                          label="Anti-Passback (default ON)"
                        />
                      </Stack>

                      <Box sx={{ mt: 3 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleSaveAutoInductionSettings}
                          disabled={savingAutoInduction || !site}
                          startIcon={savingAutoInduction ? <CircularProgress size={18} /> : null}
                        >
                          {savingAutoInduction ? 'Saving...' : 'Save Auto-Induction Settings'}
                        </Button>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>
          </>
        )}
      </Container>

      {/* Confirmation dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
}
